import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Building2, Warehouse, Wrench, Users, Cpu, Fuel,
  MapPin, Clock, Gauge, Radio, Shield, Check, Zap, Home
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ServiceCategory } from '@/types/serviceWizard';
import HelicopterSilhouetteIcon from '@/components/icons/HelicopterSilhouetteIcon';
import { formatShiftCoverage } from '@/utils/serviceFormatting';

interface ServiceFacilityShowcaseProps {
  listing: any;
  category: ServiceCategory;
}

const ServiceFacilityShowcase: React.FC<ServiceFacilityShowcaseProps> = ({ listing, category }) => {
  // MRO/CAMO Facility
  const renderMROFacility = () => (
    <div className="space-y-4">
      {/* Facility Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {listing.facility_size_sqm && (
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/10 border-blue-200/50">
            <CardContent className="p-4 text-center">
              <Warehouse className="w-6 h-6 mx-auto mb-2 text-blue-600 dark:text-blue-400" />
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                {listing.facility_size_sqm.toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">Sq. Meters</p>
            </CardContent>
          </Card>
        )}
        {listing.hangar_capacity && (
          <Card className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/20 dark:to-purple-950/10 border-violet-200/50">
            <CardContent className="p-4 text-center">
              <HelicopterSilhouetteIcon className="w-6 h-6 mx-auto mb-2" />
              <p className="text-2xl font-bold text-violet-700 dark:text-violet-300">
                {listing.hangar_capacity}
              </p>
              <p className="text-xs text-muted-foreground">Aircraft Capacity</p>
            </CardContent>
          </Card>
        )}
        {listing.shift_coverage && (
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/20 dark:to-green-950/10 border-emerald-200/50">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 mx-auto mb-2 text-emerald-600 dark:text-emerald-400" />
              <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                {formatShiftCoverage(listing.shift_coverage)}
              </p>
              <p className="text-xs text-muted-foreground">Shift Coverage</p>
            </CardContent>
          </Card>
        )}
        {listing.aog_support && (
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/10 border-amber-200/50">
            <CardContent className="p-4 text-center">
              <Zap className="w-6 h-6 mx-auto mb-2 text-amber-600 dark:text-amber-400" />
              <p className="text-lg font-bold text-amber-700 dark:text-amber-300">
                {listing.aog_response_time || 'Available'}
              </p>
              <p className="text-xs text-muted-foreground">AOG Response</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Equipment List */}
      {listing.equipment_list?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="w-4 h-4 text-primary" />
              Equipment & Tooling
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {listing.equipment_list.map((item: string, idx: number) => (
                <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-muted/50 rounded-lg">
                  <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <span className="truncate">{item}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Training Facility
  const renderTrainingFacility = () => (
    <div className="space-y-4">
      {/* Training Stats */}
      <div className="grid grid-cols-3 gap-3">
        {listing.instructor_count && (
          <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 border-green-200/50">
            <CardContent className="p-4 text-center">
              <Users className="w-6 h-6 mx-auto mb-2 text-green-600 dark:text-green-400" />
              <p className="text-2xl font-bold text-green-700 dark:text-green-300">{listing.instructor_count}</p>
              <p className="text-xs text-muted-foreground">Instructors</p>
            </CardContent>
          </Card>
        )}
        {listing.fleet_size && (
          <Card className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/20 border-sky-200/50">
            <CardContent className="p-4 text-center">
              <HelicopterSilhouetteIcon className="w-6 h-6 mx-auto mb-2" />
              <p className="text-2xl font-bold text-sky-700 dark:text-sky-300">{listing.fleet_size}</p>
              <p className="text-xs text-muted-foreground">Training Aircraft</p>
            </CardContent>
          </Card>
        )}
        {listing.simulator_types?.length > 0 && (
          <Card className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/20 border-violet-200/50">
            <CardContent className="p-4 text-center">
              <Cpu className="w-6 h-6 mx-auto mb-2 text-violet-600 dark:text-violet-400" />
              <p className="text-2xl font-bold text-violet-700 dark:text-violet-300">{listing.simulator_types.length}</p>
              <p className="text-xs text-muted-foreground">Simulators</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Simulator Types */}
      {listing.simulator_types?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Cpu className="w-4 h-4 text-violet-600 dark:text-violet-400" />
              Simulator Fleet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {listing.simulator_types.map((sim: string, idx: number) => (
                <Badge
                  key={idx}
                  className="bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                >
                  {sim}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Managed Aircraft */}
      {listing.managed_aircraft?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <HelicopterSilhouetteIcon className="w-4 h-4" />
              Training Fleet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {listing.managed_aircraft.map((aircraft: string, idx: number) => (
                <Badge key={idx} variant="outline">
                  {aircraft}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // FBO/Heliport Facility
  const renderFBOFacility = () => (
    <div className="space-y-4">
      {/* Key Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {listing.runway_length_ft && (
          <Card className="bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-950/20 border-slate-200/50">
            <CardContent className="p-4 text-center">
              <Gauge className="w-6 h-6 mx-auto mb-2 text-slate-600 dark:text-slate-400" />
              <p className="text-2xl font-bold text-slate-700 dark:text-slate-300">{listing.runway_length_ft.toLocaleString()}'</p>
              <p className="text-xs text-muted-foreground">Runway Length</p>
            </CardContent>
          </Card>
        )}
        {listing.helipad_count && (
          <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-950/20 border-teal-200/50">
            <CardContent className="p-4 text-center">
              <Home className="w-6 h-6 mx-auto mb-2 text-teal-600 dark:text-teal-400" />
              <p className="text-2xl font-bold text-teal-700 dark:text-teal-300">{listing.helipad_count}</p>
              <p className="text-xs text-muted-foreground">Helipads</p>
            </CardContent>
          </Card>
        )}
        {listing.hangar_capacity && (
          <Card className="bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/20 border-indigo-200/50">
            <CardContent className="p-4 text-center">
              <Warehouse className="w-6 h-6 mx-auto mb-2 text-indigo-600 dark:text-indigo-400" />
              <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">{listing.hangar_capacity}</p>
              <p className="text-xs text-muted-foreground">Hangar Spots</p>
            </CardContent>
          </Card>
        )}
        {listing.parking_spots && (
          <Card className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-950/20 border-blue-200/50">
            <CardContent className="p-4 text-center">
              <MapPin className="w-6 h-6 mx-auto mb-2 text-blue-600 dark:text-blue-400" />
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{listing.parking_spots}</p>
              <p className="text-xs text-muted-foreground">Parking Spots</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Fuel Types */}
      {listing.fuel_types?.length > 0 && (
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
              <Fuel className="w-4 h-4" />
              Fuel Services
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {listing.fuel_types.map((fuel: string, idx: number) => (
                <Badge
                  key={idx}
                  className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-sm px-3 py-1"
                >
                  <Fuel className="w-3 h-3 mr-1" />
                  {fuel}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Amenities */}
      {listing.amenities?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Amenities & Services</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {listing.amenities.map((amenity: string, idx: number) => (
                <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-muted/50 rounded-lg">
                  <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <span>{amenity}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // UAS Facility
  const renderUASFacility = () => (
    <div className="space-y-4">
      {/* Drone Fleet */}
      {listing.drone_types?.length > 0 && (
        <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 dark:from-cyan-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-cyan-700 dark:text-cyan-400">
              <Radio className="w-4 h-4" />
              Drone Fleet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {listing.drone_types.map((drone: string, idx: number) => (
                <Badge
                  key={idx}
                  className="bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 px-3 py-1"
                >
                  {drone}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payload Capabilities */}
      {listing.payload_capabilities?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="w-4 h-4 text-primary" />
              Payload Capabilities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {listing.payload_capabilities.map((payload: string, idx: number) => (
                <Badge key={idx} variant="secondary">
                  {payload}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Flight Authorizations */}
      {listing.flight_authorizations?.length > 0 && (
        <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
              <Shield className="w-4 h-4" />
              Flight Authorizations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {listing.flight_authorizations.map((auth: string, idx: number) => (
                <Badge
                  key={idx}
                  className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                >
                  <Check className="w-3 h-3 mr-1" />
                  {auth}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Operator Facility
  const renderOperatorFacility = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {listing.fleet_size && (
          <Card className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/20 border-sky-200/50">
            <CardContent className="p-4 text-center">
              <HelicopterSilhouetteIcon className="w-6 h-6 mx-auto mb-2" />
              <p className="text-2xl font-bold text-sky-700 dark:text-sky-300">{listing.fleet_size}</p>
              <p className="text-xs text-muted-foreground">Aircraft Fleet</p>
            </CardContent>
          </Card>
        )}
        {listing.service_radius && (
          <Card className="bg-gradient-to-br from-indigo-50 to-violet-50 dark:from-indigo-950/20 border-indigo-200/50">
            <CardContent className="p-4 text-center">
              <Gauge className="w-6 h-6 mx-auto mb-2 text-indigo-600 dark:text-indigo-400" />
              <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">{listing.service_radius} km</p>
              <p className="text-xs text-muted-foreground">Service Range</p>
            </CardContent>
          </Card>
        )}
        {listing.bases?.length > 0 && (
          <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-950/20 border-teal-200/50">
            <CardContent className="p-4 text-center">
              <Building2 className="w-6 h-6 mx-auto mb-2 text-teal-600 dark:text-teal-400" />
              <p className="text-2xl font-bold text-teal-700 dark:text-teal-300">{listing.bases.length}</p>
              <p className="text-xs text-muted-foreground">Operating Bases</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Mission Types */}
      {listing.mission_types?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Mission Capabilities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {listing.mission_types.map((mission: string, idx: number) => (
                <Badge
                  key={idx}
                  className="bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400"
                >
                  {mission}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Managed Aircraft */}
      {listing.managed_aircraft?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <HelicopterSilhouetteIcon className="w-4 h-4" />
              Fleet Roster
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {listing.managed_aircraft.map((aircraft: string, idx: number) => (
                <Badge key={idx} variant="outline">
                  {aircraft}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Engineering Facility
  const renderEngineeringFacility = () => (
    <div className="space-y-4">
      {/* Active STCs */}
      {listing.active_stcs_count && (
        <Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-950/20 border-orange-200/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-orange-100 dark:bg-orange-900/30">
                <Shield className="w-8 h-8 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-orange-700 dark:text-orange-300">{listing.active_stcs_count}</p>
                <p className="text-sm text-muted-foreground">Active STCs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Software Tools */}
      {listing.software_tools?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Cpu className="w-4 h-4 text-primary" />
              Software & Tools
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {listing.software_tools.map((tool: string, idx: number) => (
                <Badge key={idx} variant="outline" className="px-3 py-1">
                  {tool}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* OEM Partnerships */}
      {listing.oem_partnerships?.length > 0 && (
        <Card className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
              <Shield className="w-4 h-4" />
              OEM Partnerships
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {listing.oem_partnerships.map((oem: string, idx: number) => (
                <Badge
                  key={idx}
                  className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                >
                  {oem}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Specializations */}
      {listing.engineering_specializations?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Wrench className="w-4 h-4 text-primary" />
              Specializations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {listing.engineering_specializations.map((spec: string, idx: number) => (
                <Badge key={idx} variant="secondary">
                  {spec}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Check if there's any facility data to show
  const hasFacilityData = () => {
    switch (category) {
      case 'mro':
      case 'camo':
        return listing.facility_size_sqm || listing.hangar_capacity || listing.equipment_list?.length > 0;
      case 'training':
        return listing.instructor_count || listing.fleet_size || listing.simulator_types?.length > 0;
      case 'fbo_heliport':
        return listing.fuel_types?.length > 0 || listing.amenities?.length > 0 || listing.runway_length_ft;
      case 'uas':
        return listing.drone_types?.length > 0 || listing.payload_capabilities?.length > 0;
      case 'operator':
        return listing.fleet_size || listing.mission_types?.length > 0 || listing.managed_aircraft?.length > 0;
      case 'engineering':
        return listing.software_tools?.length > 0 || listing.oem_partnerships?.length > 0 || listing.active_stcs_count;
      default:
        return false;
    }
  };

  if (!hasFacilityData()) return null;

  return (
    <div className="space-y-4">
      {(category === 'mro' || category === 'camo') && renderMROFacility()}
      {category === 'training' && renderTrainingFacility()}
      {category === 'fbo_heliport' && renderFBOFacility()}
      {category === 'uas' && renderUASFacility()}
      {category === 'operator' && renderOperatorFacility()}
      {category === 'engineering' && renderEngineeringFacility()}
    </div>
  );
};

export default ServiceFacilityShowcase;
